import { z } from 'zod';
import { DomainError } from '../errors.js';
import { addMonths, compareLocalDate, daysBetween, parseLocalDate } from '../local-date.js';
import type { ParseResult, SchemaIssue } from '../voyage/schemas.js';
import {
  bandOf,
  LEGACY_RISK_MATRIX,
  RA_BAND_KEYS,
  type RaBandKey,
  type RaMatrixDefinition,
  type RaMatrixSnapshot,
  type RiskRating,
  ratingIn,
} from './matrix.js';

export type { RiskRating } from './matrix.js';

/**
 * Risk değerlendirmesi kuralları (ADR 0050; ISM 1.2.2.2, ISM 7). Her tehlike 5×5 matriste iki kez
 * puanlanır: ilk risk (mevcut önlemlerle) ve kontrol sonrası (residual) risk. Değerlendirme gemide
 * yazılır, gönderilince değiştirilemez revizyon olur; onay revizyonun içerik hash'ine bağlıdır.
 * Şema gemi sunucusunda, bulutta ve formda ortaktır; hata iletileri koddur. Modül saftır: bugünün
 * gemi günü parametreyle gelir.
 */

/** Olasılık ve şiddet ölçeği (1–5; referans "Risk Scoring Guide"). */
export const RISK_SCALE = [1, 2, 3, 4, 5] as const;

/**
 * Seviyeler (bant adları), düşükten yükseğe: Legacy 5-band'in beşi ve ortağın Critical'i
 * (ADR 0085 §2). Bir matris sürümü bunların bir alt kümesini kullanır.
 */
export const RISK_LEVELS = RA_BAND_KEYS;
export type RiskLevel = RaBandKey;

/** Legacy 5-band (ADR 0050 §2) seviye adları. */
export type LegacyRiskLevel = 'very_low' | 'low' | 'medium' | 'high' | 'very_high';

/** Legacy 5-band seviye bantları, skor aralığı (dahil) [ADR 0050 §2]; `LEGACY_RISK_MATRIX`. */
export const RISK_LEVEL_BANDS: Readonly<Record<LegacyRiskLevel, readonly [number, number]>> = {
  very_low: [1, 2],
  low: [3, 5],
  medium: [6, 9],
  high: [10, 15],
  very_high: [16, 25],
};

/** Legacy 5-band: kontrol önlemi gereken en düşük ilk risk skoru, Medium (ADR 0050 §3). */
export const CONTROLS_REQUIRED_FROM = 6;
/** Legacy 5-band: kabul edilemez kalan risk, Very High (ADR 0050 §3). */
export const INTOLERABLE_RESIDUAL_FROM = 16;
/** Gözden geçirme en çok bu kadar ay sonra (ADR 0050 §3; ajan kararı). */
export const MAX_REVIEW_MONTHS = 12;
/** Gözden geçirmesi yaklaşan: bu kadar gün içinde (ADR 0050 §4). */
export const REVIEW_DUE_DAYS = 30;

/** Departmanlar (referans listesi: Deck, Engine, Catering, Operations, Crew). */
export const RA_DEPARTMENTS = [
  'deck',
  'engine',
  'catering',
  'operations',
  'crew',
  'other',
] as const;
export type RaDepartment = (typeof RA_DEPARTMENTS)[number];

/** Risk kategorileri (referans senaryoları ve COSWP'nin tipik işleri). */
export const RA_CATEGORIES = [
  'mooring',
  'navigation',
  'cargo',
  'fire',
  'chemical',
  'work_at_height',
  'enclosed_space',
  'hot_work',
  'equipment',
  'fatigue',
  'security',
  'other',
] as const;
export type RaCategory = (typeof RA_CATEGORIES)[number];

/** Kontrol önlemi türü: kontrol hiyerarşisi ve eğitim (referans: Engineering, Administrative, PPE, Training). */
export const CONTROL_TYPES = [
  'elimination',
  'substitution',
  'engineering',
  'administrative',
  'ppe',
  'training',
] as const;
export type ControlType = (typeof CONTROL_TYPES)[number];

/** Önlemin durumu (referans: Planned, Open, In Progress; tamamlanan Done). */
export const CONTROL_STATUSES = ['planned', 'open', 'in_progress', 'done'] as const;
export type ControlStatus = (typeof CONTROL_STATUSES)[number];

const MAX_HAZARDS = 50;
const MAX_CONTROLS = 100;

// ---------------------------------------------------------------------------------------------
// Skor

function scaleValue(value: number, field: string): number {
  if (!Number.isInteger(value) || value < 1 || value > 5) {
    throw new DomainError('VALIDATION_FAILED', `${field} ${value}`, field);
  }
  return value;
}

/** Skor = olasılık × şiddet (1–25). */
export function riskScore(likelihood: number, severity: number): number {
  return scaleValue(likelihood, 'likelihood') * scaleValue(severity, 'severity');
}

/** Legacy 5-band seviyesi (ADR 0050 §2); matrisli seviye `bandOf`. */
export function riskLevel(score: number): LegacyRiskLevel {
  return bandOf(LEGACY_RISK_MATRIX, score).key as LegacyRiskLevel;
}

/** Puan ve seviye; matris verilmezse Legacy 5-band (ADR 0085 §2). */
export function riskRating(
  likelihood: number,
  severity: number,
  matrix: RaMatrixDefinition = LEGACY_RISK_MATRIX,
): RiskRating {
  return ratingIn(matrix, likelihood, severity);
}

// ---------------------------------------------------------------------------------------------
// Şema

const ID = /^[A-Za-z0-9-]{1,40}$/;

const text = (max: number) =>
  z
    .string({ error: 'REQUIRED' })
    .transform((v) => v.trim())
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));
const optionalText = (max: number) =>
  z
    .string()
    .transform((v) => v.trim())
    .pipe(z.string().max(max, 'TOO_LONG'));

function isLocalDate(value: string): boolean {
  try {
    parseLocalDate(value);
    return true;
  } catch {
    return false;
  }
}
const localDate = z.string({ error: 'REQUIRED' }).refine(isLocalDate, 'INVALID_DATE');
const scale = z
  .number({ error: 'REQUIRED' })
  .int('INVALID')
  .min(1, 'OUT_OF_RANGE')
  .max(5, 'OUT_OF_RANGE');
const id = z.string({ error: 'REQUIRED' }).regex(ID, 'INVALID');

export const riskHazardSchema = z.strictObject({
  id,
  hazard: text(300),
  consequence: optionalText(500),
  likelihood: scale,
  severity: scale,
  residualLikelihood: scale,
  residualSeverity: scale,
});
export type RiskHazard = z.output<typeof riskHazardSchema>;

export const riskControlSchema = z.strictObject({
  id,
  hazardId: id.nullable(),
  type: z.enum(CONTROL_TYPES, { error: 'INVALID' }),
  measure: text(300),
  responsible: text(120),
  dueDay: localDate.nullable(),
  status: z.enum(CONTROL_STATUSES, { error: 'INVALID' }),
});
export type RiskControl = z.output<typeof riskControlSchema>;

const common = {
  title: text(200),
  activity: optionalText(2000),
  department: z.enum(RA_DEPARTMENTS, { error: 'INVALID' }),
  category: z.enum(RA_CATEGORIES, { error: 'INVALID' }),
  hazards: z.array(riskHazardSchema).max(MAX_HAZARDS, 'TOO_MANY'),
};

/** Değerlendirmenin içeriği (taslak düzeyi): çalışma kopyası ve revizyonun `content`'i. */
export const riskAssessmentSchema = z.strictObject({
  ...common,
  location: optionalText(200).nullable(),
  assessmentDay: localDate,
  reviewDay: localDate,
  controls: z.array(riskControlSchema).max(MAX_CONTROLS, 'TOO_MANY'),
});
export type RiskAssessmentContent = z.output<typeof riskAssessmentSchema>;

/** Şirketin genel RA şablonu (ADR 0050 §5): gün ve yer yok; önlemin son tarihi yok. */
export const riskTemplateSchema = z.strictObject({
  code: text(20),
  ...common,
  controls: z
    .array(riskControlSchema.extend({ dueDay: z.null({ error: 'INVALID' }) }))
    .max(MAX_CONTROLS, 'TOO_MANY'),
});
export type RiskTemplateContent = z.output<typeof riskTemplateSchema>;

function toIssues(error: z.ZodError): SchemaIssue[] {
  return error.issues.map((issue) => ({
    path: issue.path.map(String).join('.'),
    code: issue.message,
  }));
}

export function parseRiskAssessment(input: unknown): ParseResult<RiskAssessmentContent> {
  const result = riskAssessmentSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

export function parseRiskTemplate(input: unknown): ParseResult<RiskTemplateContent> {
  const result = riskTemplateSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

/** Şema hatasını tek hata koduna çevirir (`INVALID_RISK_ASSESSMENT`, ayrıntı alan:kod listesi). */
export function riskAssessmentOrThrow(input: unknown): RiskAssessmentContent {
  const parsed = parseRiskAssessment(input);
  if (!parsed.ok) {
    throw new DomainError(
      'INVALID_RISK_ASSESSMENT',
      parsed.issues.map((i) => `${i.path}:${i.code}`).join(','),
    );
  }
  return parsed.value;
}

export function riskTemplateOrThrow(input: unknown): RiskTemplateContent {
  const parsed = parseRiskTemplate(input);
  if (!parsed.ok) {
    throw new DomainError(
      'INVALID_RISK_TEMPLATE',
      parsed.issues.map((i) => `${i.path}:${i.code}`).join(','),
    );
  }
  return parsed.value;
}

// ---------------------------------------------------------------------------------------------
// Özet ve gönderim

/** Değerlendirmenin ilk ve kalan riski: en yüksek skorlu tehlike (eşitlikte ilk sıradaki). */
export function riskSummary(
  hazards: readonly Pick<
    RiskHazard,
    'likelihood' | 'severity' | 'residualLikelihood' | 'residualSeverity'
  >[],
  matrix: RaMatrixDefinition = LEGACY_RISK_MATRIX,
): {
  initial: RiskRating | null;
  residual: RiskRating | null;
} {
  let initial: RiskRating | null = null;
  let residual: RiskRating | null = null;
  for (const h of hazards) {
    const i = riskRating(h.likelihood, h.severity, matrix);
    const r = riskRating(h.residualLikelihood, h.residualSeverity, matrix);
    if (!initial || i.score > initial.score) initial = i;
    if (!residual || r.score > residual.score) residual = r;
  }
  return { initial, residual };
}

/**
 * Gönderim kuralları (ADR 0050 §3); taslak eksik olabilir, gönderim olamaz. Sıra: açıklama,
 * kimlikler, tehlike, kalan ≤ ilk, önlem, kabul edilemez kalan risk, tarihler. Önlem şartı ve
 * kabul edilemez kalan risk matris sürümünün bandından (ADR 0085 §3): ilk riskin bandı
 * "kabul edilebilir" değilse önlem şart; kalan riskin bandı "kabul edilemez" ise gönderilemez.
 * Matris verilmezse Legacy 5-band (≥ 6 önlem, ≥ 16 kabul edilemez).
 */
export function assertRiskAssessmentSubmittable(
  content: RiskAssessmentContent,
  today: string,
  matrix: RaMatrixDefinition = LEGACY_RISK_MATRIX,
): void {
  if (content.activity.trim() === '') throw new DomainError('ACTIVITY_REQUIRED', 'activity');
  const ids = new Set<string>();
  for (const item of [...content.hazards, ...content.controls]) {
    if (ids.has(item.id)) throw new DomainError('DUPLICATE_ID', item.id);
    ids.add(item.id);
  }
  if (content.hazards.length === 0) throw new DomainError('NO_HAZARDS');
  const hazardIds = new Set(content.hazards.map((h) => h.id));
  for (const c of content.controls) {
    if (c.hazardId !== null && !hazardIds.has(c.hazardId)) {
      throw new DomainError('UNKNOWN_HAZARD', `${c.id} → ${c.hazardId}`);
    }
  }
  for (const h of content.hazards) {
    const initial = riskScore(h.likelihood, h.severity);
    const residual = riskScore(h.residualLikelihood, h.residualSeverity);
    if (residual > initial) throw new DomainError('RESIDUAL_ABOVE_INITIAL', h.id);
    if (
      bandOf(matrix, initial).acceptance !== 'acceptable' &&
      !content.controls.some((c) => c.hazardId === h.id)
    ) {
      throw new DomainError('CONTROLS_REQUIRED', h.id);
    }
    if (bandOf(matrix, residual).acceptance === 'unacceptable') {
      throw new DomainError('RESIDUAL_INTOLERABLE', h.id);
    }
  }
  if (compareLocalDate(content.assessmentDay, today) > 0) {
    throw new DomainError('FUTURE_DATE', content.assessmentDay, 'assessmentDay');
  }
  if (compareLocalDate(content.reviewDay, content.assessmentDay) <= 0) {
    throw new DomainError('REVIEW_BEFORE_ASSESSMENT', content.reviewDay, 'reviewDay');
  }
  if (
    compareLocalDate(content.reviewDay, addMonths(content.assessmentDay, MAX_REVIEW_MONTHS)) > 0
  ) {
    throw new DomainError('REVIEW_TOO_LATE', content.reviewDay, 'reviewDay');
  }
}

/**
 * Gözden geçirme durumu: geçmişse `overdue`, `dueDays` (varsayılan 30; şirket ayarı K7, ADR 0095)
 * gün içindeyse `due`.
 */
export function reviewStatus(
  reviewDay: string,
  today: string,
  dueDays: number = REVIEW_DUE_DAYS,
): 'ok' | 'due' | 'overdue' {
  const days = daysBetween(today, reviewDay);
  if (days < 0) return 'overdue';
  return days <= dueDays ? 'due' : 'ok';
}

/** Numara `RA-YYYY-NNN` (ADR 0050 §1): gemi ve yıl başına boşluksuz; gemi sunucusu verir. */
export function nextRiskAssessmentNumber(
  day: string,
  lastSeq: number,
): { raYear: number; raSeq: number; raNo: string } {
  const { year } = parseLocalDate(day);
  if (!Number.isInteger(lastSeq) || lastSeq < 0) {
    throw new DomainError('VALIDATION_FAILED', `last_seq ${lastSeq}`, 'ra_seq');
  }
  const raSeq = lastSeq + 1;
  return {
    raYear: year,
    raSeq,
    raNo: `RA-${String(year).padStart(4, '0')}-${String(raSeq).padStart(3, '0')}`,
  };
}

export interface RiskAssessmentContentInput {
  assessmentId: string;
  raNo: string;
  revisionNo: number;
  templateId: string | null;
  templateVersion: number | null;
  content: RiskAssessmentContent;
  /**
   * Revizyonun matris sürümü (ADR 0085 §4): yeni revizyonlarda anlık görüntü. `null` ya da yoksa
   * içerikte `matrix` anahtarı olmaz: eski revizyonların içeriği ve hash'i aynen kalır, Legacy
   * 5-band ile okunur.
   */
  matrix?: RaMatrixSnapshot | null;
  /**
   * Başladığı yayımlı şirket sürümü (ADR 0087 §5): yeni değerlendirmelerde. `null` ya da yoksa
   * içerikte anahtar olmaz: eski revizyonların içeriği ve hash'i aynen kalır.
   */
  sourceVersionId?: string | null;
}

/**
 * Revizyonun içerik nesnesi (ADR 0050 §4): `content_hash` = SHA-256(JCS(nesne)); hash'i
 * `packages/db` hesaplar. Revizyon no girer: her onay yalnız kendi revizyonuna bağlıdır.
 */
export function riskAssessmentContent(input: RiskAssessmentContentInput) {
  if (!Number.isInteger(input.revisionNo) || input.revisionNo < 1) {
    throw new DomainError('VALIDATION_FAILED', `revision_no ${input.revisionNo}`, 'revision_no');
  }
  const c = input.content;
  return {
    assessmentId: input.assessmentId,
    raNo: input.raNo,
    revisionNo: input.revisionNo,
    templateId: input.templateId,
    templateVersion: input.templateVersion,
    title: c.title,
    activity: c.activity,
    department: c.department,
    category: c.category,
    location: c.location,
    assessmentDay: c.assessmentDay,
    reviewDay: c.reviewDay,
    hazards: c.hazards.map((h) => ({ ...h })),
    controls: c.controls.map((x) => ({ ...x })),
    ...(input.matrix
      ? {
          matrix: {
            versionId: input.matrix.versionId,
            version: input.matrix.version,
            definition: input.matrix.definition,
          },
        }
      : {}),
    ...(input.sourceVersionId ? { sourceVersionId: input.sourceVersionId } : {}),
  };
}
export type RiskAssessmentRevisionContent = ReturnType<typeof riskAssessmentContent>;

/** İçerik (revizyon no dışında) değişti mi; `NO_CHANGE` için. */
export function sameRiskContent(a: RiskAssessmentContent, b: RiskAssessmentContent): boolean {
  const pick = (c: RiskAssessmentContent) =>
    JSON.stringify(
      riskAssessmentContent({
        assessmentId: '',
        raNo: '',
        revisionNo: 1,
        templateId: null,
        templateVersion: null,
        content: c,
      }),
    );
  return pick(a) === pick(b);
}

/**
 * Şablondan gemiye özel kopya (ADR 0050 §5): tehlike ve önlemler yeni kimlik alır, önlemin bağı
 * yeni kimliğe taşınır; önlemler `planned` başlar.
 */
export function copyFromTemplate(
  template: Pick<RiskTemplateContent, 'hazards' | 'controls'>,
  newId: () => string,
): Pick<RiskAssessmentContent, 'hazards' | 'controls'> {
  const ids = new Map<string, string>();
  const hazards = template.hazards.map((h) => {
    const next = newId();
    ids.set(h.id, next);
    return { ...h, id: next };
  });
  const controls = template.controls.map((c) => ({
    ...c,
    id: newId(),
    hazardId: c.hazardId === null ? null : (ids.get(c.hazardId) ?? null),
    dueDay: null,
    status: 'planned' as const,
  }));
  return { hazards, controls };
}
